import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Crown, Home } from 'lucide-react';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/subscription/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
        } else if (res.data.status === 'expired') {
          setStatus('failed');
        } else {
          // Continue polling
          setAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setAttempts(prev => prev + 1);
        if (attempts < maxAttempts - 1) {
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollPaymentStatus();
  }, [searchParams, attempts, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="card-dark p-12" data-testid="checking-status">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-cyan-400 animate-spin" />
            <h1 className="font-heading text-2xl font-bold mb-4">Processing Payment</h1>
            <p className="text-white/50">Please wait while we confirm your subscription...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="card-dark p-12 animate-fade-in-up" data-testid="success-status">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">Welcome to Premium!</h1>
            <p className="text-white/60 mb-8">
              Your subscription is now active. You have unlimited access to all premium features.
            </p>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4 mb-8">
              <div className="flex items-center justify-center gap-2 text-yellow-500">
                <Crown className="w-5 h-5" />
                <span className="font-mono text-sm uppercase">Premium Member</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button onClick={() => navigate('/analyzer')} className="w-full btn-primary" data-testid="go-analyzer-btn">
                Try AI Analyzer
              </Button>
              <Button onClick={() => navigate('/')} variant="ghost" className="w-full text-white/60 hover:text-white" data-testid="go-home-btn">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="card-dark p-12 animate-fade-in-up" data-testid="failed-status">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">Payment Issue</h1>
            <p className="text-white/60 mb-8">
              We couldn't confirm your payment. Please try again or contact support if you were charged.
            </p>
            
            <div className="space-y-3">
              <Button onClick={() => navigate('/subscription')} className="w-full btn-primary" data-testid="retry-btn">
                Try Again
              </Button>
              <Button onClick={() => navigate('/')} variant="ghost" className="w-full text-white/60 hover:text-white" data-testid="go-home-btn">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
